import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card, PrimaryButton, GhostButton } from "@/components/Primitives";
import { toast } from "sonner";
import { Database, Broom, ShieldCheck, Warning } from "@phosphor-icons/react";

const SEED_CONFIRM = "SEED_V2_STARTER_TEMPLATES";

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-white/5 text-[12px]">
      <span className="text-zinc-500">{label}</span>
      <span className={accent ? "text-[#FFB300] font-mono-data" : "text-zinc-200 font-mono-data"}>{value}</span>
    </div>
  );
}

export default function MaintenancePanel() {
  const [seedDry, setSeedDry] = useState(null);
  const [seedResult, setSeedResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState("");
  const [purge, setPurge] = useState(null);

  const runSeedDry = async () => {
    setBusy("seed-dry");
    try {
      const { data } = await api.post("/admin/tasks/seed-templates-dryrun");
      setSeedDry(data); setSeedResult(null);
      toast.success("Dry-run complete — no records written");
    } catch (e) { toast.error(e?.response?.data?.detail || "Dry-run failed"); }
    finally { setBusy(""); }
  };

  const runSeed = async () => {
    if (!seedDry || !confirmed) return;
    setBusy("seed");
    try {
      const { data } = await api.post("/admin/tasks/seed-templates", { confirm: SEED_CONFIRM });
      setSeedResult(data); setConfirmed(false);
      toast.success(`Seeded ${data.created_search_rules} rules + ${data.created_search_intelligence} intel`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
    finally { setBusy(""); }
  };

  const runPurgeDry = async () => {
    setBusy("purge");
    try {
      const { data } = await api.post("/admin/tasks/purge-demo-dryrun");
      setPurge(data);
      toast.success("Purge dry-run complete — nothing deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge dry-run failed"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="maintenance-section">
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Maintenance · Owner / Admin</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* V2 Starter Templates */}
        <Card className="p-5" data-testid="maint-seed-card">
          <div className="flex items-center gap-2 mb-1">
            <Database size={15} className="text-[#FFB300]" />
            <span className="text-sm text-white">V2 Starter Templates</span>
          </div>
          <p className="text-[11px] text-zinc-500 mb-3">Creates real <span className="text-zinc-300">is_demo=false</span> Search Rules + Search Intelligence records. Idempotent. Never creates Opportunities/Candidates and never touches users/suppliers/documents.</p>

          <div className="flex items-center gap-2 mb-3">
            <GhostButton onClick={runSeedDry} disabled={busy === "seed-dry"} data-testid="maint-seed-dryrun-btn">
              {busy === "seed-dry" ? "Running…" : "Dry Run"}
            </GhostButton>
            <PrimaryButton onClick={runSeed} disabled={!seedDry || !confirmed || busy === "seed"} data-testid="maint-seed-btn">
              {busy === "seed" ? "Seeding…" : "Seed Templates"}
            </PrimaryButton>
          </div>

          {seedDry && (
            <div className="mb-3" data-testid="maint-seed-output">
              <div className="text-[9px] tracking-widest uppercase text-zinc-500 mb-1">Dry-Run Result (nothing written)</div>
              <Row label="Would create — Search Rules" value={seedDry.would_create_search_rules} accent />
              <Row label="Would create — Search Intelligence" value={seedDry.would_create_search_intelligence} accent />
              <Row label="Already existing — Rules" value={seedDry.already_existing?.search_rules} />
              <Row label="Already existing — Intelligence" value={seedDry.already_existing?.search_intelligence} />
              <Row label="Warnings" value={(seedDry.warnings || []).length} />
              <label className="flex items-start gap-2 text-[11px] text-zinc-300 mt-3 cursor-pointer">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="maint-seed-confirm-check" className="mt-0.5" />
                I confirm seeding real <span className="text-zinc-100">is_demo=false</span> starter records into this workspace.
              </label>
            </div>
          )}

          {seedResult && (
            <div className="border border-[#00E676]/30 bg-[#00E676]/[0.05] px-3 py-2" data-testid="maint-seed-result">
              <div className="text-[9px] tracking-widest uppercase text-[#00E676] mb-1">Seed Complete</div>
              <Row label="Created — Search Rules" value={seedResult.created_search_rules} accent />
              <Row label="Created — Search Intelligence" value={seedResult.created_search_intelligence} accent />
              <Row label="Skipped (already existed) — Rules" value={seedResult.already_existing?.search_rules} />
              <Row label="Skipped (already existed) — Intelligence" value={seedResult.already_existing?.search_intelligence} />
            </div>
          )}
        </Card>

        {/* Demo Purge Dry Run */}
        <Card className="p-5" data-testid="maint-purge-card">
          <div className="flex items-center gap-2 mb-1">
            <Broom size={15} className="text-[#FFB300]" />
            <span className="text-sm text-white">Demo Purge — Dry Run</span>
          </div>
          <p className="text-[11px] text-zinc-500 mb-3">Previews which demo records & demo users would be removed. Real users (Mario/Will/Philipp) and is_demo=false V2 starters are preserved.</p>

          <GhostButton onClick={runPurgeDry} disabled={busy === "purge"} data-testid="maint-purge-dryrun-btn">
            {busy === "purge" ? "Running…" : "Dry Run"}
          </GhostButton>

          <div className="border border-[#FFB300]/30 bg-[#FFB300]/[0.05] px-3 py-2 mt-3 text-[11px] text-[#FFB300] flex items-center gap-2" data-testid="maint-no-purge-confirm-note">
            <Warning size={13} /> This is dry-run only. No records were deleted. Confirm-purge is intentionally not available in the app yet.
          </div>

          {purge && (
            <div className="mt-3" data-testid="maint-purge-output">
              <div className="text-[9px] tracking-widest uppercase text-zinc-500 mb-1">Would Be Removed (by collection)</div>
              {Object.keys(purge.records_that_would_be_removed || {}).length === 0
                ? <div className="text-[12px] text-zinc-500 py-1">No demo records found.</div>
                : Object.entries(purge.records_that_would_be_removed).map(([k, v]) => <Row key={k} label={k} value={v} />)}
              <Row label="Demo users that would be removed" value={(purge.demo_users_that_would_be_removed || []).length} />

              <div className="text-[9px] tracking-widest uppercase text-zinc-500 mt-3 mb-1">Preserved</div>
              <Row label="V2 starters — Search Rules" value={purge.v2_starter_records_preserved?.search_rules} accent />
              <Row label="V2 starters — Search Intelligence" value={purge.v2_starter_records_preserved?.search_intelligence} accent />
              <Row label="Real users preserved" value={(purge.real_users_preserved || []).length} />
              <div className="mt-2 space-y-1">
                {Object.entries(purge.real_named_users_present || {}).map(([em, present]) => (
                  <div key={em} className="flex items-center gap-1.5 text-[11px]">
                    <ShieldCheck size={12} className={present ? "text-[#00E676]" : "text-zinc-600"} />
                    <span className="text-zinc-400">{em}</span>
                    <span className={present ? "text-[#00E676]" : "text-zinc-600"}>{present ? "preserved" : "not present"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className="text-[11px] text-zinc-600 mt-3 space-y-0.5">
        <div>• Starter seed creates is_demo=false Search Rules and Search Intelligence records only.</div>
        <div>• Purge dry-run deletes nothing.</div>
        <div>• Confirm-purge is intentionally not available in the app yet.</div>
      </div>
    </div>
  );
}
